// CORTEX v1 UI constants (Commercial PA only).
//
// Proprietary rate matrices live in @cortex/engine and are served via
// GET /engine/config — they must never be imported here. See PR #3 and
// docs/THREAT_MODEL.md asset A1.
//
// Travel rate matrices and AI configuration are archived in
// v0.x-prerefactor-snapshot — see docs/v2/business-travel.md and
// docs/v2/admin-ai-settings.md.

import {
  type CurrentPolicyContext,
  UserRole,
  Currency,
  ProductType,
  type UserProfile,
} from './types';

export const CURRENCY_SYMBOLS: Record<Currency, string> = {
  [Currency.ZAR]: 'R',
  [Currency.USD]: '$',
  [Currency.NAD]: 'N$',
  [Currency.MUR]: 'Rs',
};

const getNextMonthFirst = (): string => {
  const d = new Date();
  const next = new Date(d.getFullYear(), d.getMonth() + 1, 1).toISOString().split('T')[0];
  return next ?? '';
};

export const getEmptyPolicyContext = (): CurrentPolicyContext => ({
  meta: {
    userRole: UserRole.Senior,
    sessionId: '',
  },
  quote_meta: {
    ref: 'New Quote',
    version: 1,
    status: 'Draft',
    productType: ProductType.CommercialPA,
    createdDate: new Date().toISOString(),
    expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
  },
  client_profile: {
    companyName: '',
    brokerName: '',
    isVatRegistered: true,
    vatNumber: '',
    address: '',
    sector: '',
    businessDescription: '',
    internalNotes: '',
    claimsHistory: 'Subject to no known or reported losses of last 3 years.',
  },
  policy_parameters: {
    currency: Currency.ZAR,
    vatEnabled: true,
    aoaLimit: 20000000,
    aolLimit: 5000000,
    brokeragePercent: 20,
    discretionaryAdjustment: 0,
    periodMonths: 12,
    startDate: getNextMonthFirst(),
    territorialLimits: 'Worldwide',
  },
  categories: [],
  pricing_summary: {
    netPremium: 0,
    grossPremium: 0,
    commissionAmount: 0,
    taxAmount: 0,
    warnings: [],
    volumeDiscountUsed: { source: 'None', discount: 0 },
  },
});

export const EMPTY_POLICY_CONTEXT = getEmptyPolicyContext();

// --- MOCK DATA ---
// MOCK_USERS is the dev-only user directory used by the mock-auth path
// (auth.state === 'unconfigured'). It is the only remaining mock fixture;
// companies are served from the real /admin/entities API endpoint.

export const MOCK_USERS: UserProfile[] = [
  {
    id: 'u1',
    name: 'System Administrator',
    email: 'admin@lombard.co.za',
    companyId: 'c1',
    jobTitle: 'Head of Systems',
    role: UserRole.Superuser,
    status: 'Active',
    permissions: {},
    lastLogin: new Date().toISOString(),
    dateCreated: new Date().toISOString(),
  },
  {
    id: 'u2',
    name: 'Sarah Jenkins',
    email: 'sarah.j@aon.co.za',
    companyId: 'c2',
    jobTitle: 'Senior Broker',
    role: UserRole.Broker,
    status: 'Active',
    permissions: {},
    lastLogin: new Date().toISOString(),
    dateCreated: new Date().toISOString(),
  },
];
